// Mind2Magic CRM — Supabase Data Layer
// All Supabase CRUD operations, auth, and realtime channel setup.

import { sb, SUPABASE_URL } from './client.js'

const DB_SCHEMA = import.meta.env.VITE_SUPABASE_SCHEMA || 'public'
let currentUser = null

export const supabaseDB = {
    isConnected() {
        return sb && SUPABASE_URL !== 'YOUR_SUPABASE_URL';
    },

    get currentUser() { return currentUser; },

    // --- AUTH ---
    async signIn(email, password) {
        if (!this.isConnected()) return { user: { email }, error: null };
        const { data, error } = await sb.auth.signInWithPassword({ email, password });
        if (error) return { user: null, error: error.message };
        currentUser = data.user;
        return { user: data.user, error: null };
    },

    async signOut() {
        if (!this.isConnected()) return;
        await sb.auth.signOut();
        currentUser = null;
    },

    async getSession() {
        if (!this.isConnected()) return null;
        const { data } = await sb.auth.getSession();
        if (data.session) currentUser = data.session.user;
        return data.session;
    },

    // --- LEADS ---
    async fetchLeads() {
        if (!this.isConnected()) return null;
        let allData = [];
        let from = 0;
        const pageSize = 1000;
        while (true) {
            const { data, error } = await sb.from('leads').select('*')
                .order('created_at', { ascending: false })
                .range(from, from + pageSize - 1);
            if (error) { console.error('fetchLeads error:', error); break; }
            if (!data || data.length === 0) break;
            allData = allData.concat(data);
            if (data.length < pageSize) break;
            from += pageSize;
        }
        return allData.length > 0 ? allData : null;
    },

    async upsertLead(lead) {
        if (!this.isConnected()) return null;
        const row = this._leadToRow(lead);
        if (row.id && (typeof row.id !== 'string' || row.id.length !== 36)) {
            delete row.id;
        }
        if (!row.id) {
            const { data, error } = await sb.from('leads').insert(row).select().maybeSingle();
            if (error) { console.error('upsertLead (insert) error:', error); return null; }
            return data;
        }
        const { data, error } = await sb.from('leads').upsert(row, { onConflict: 'id' }).select().maybeSingle();
        if (error) { console.error('upsertLead error:', error); return null; }
        return data;
    },

    async insertLeads(leadsArray, onProgress) {
        if (!this.isConnected()) return null;
        const rows = leadsArray.map(l => {
            const row = this._leadToRow(l);
            if (!row.id || typeof row.id !== 'string' || row.id.length !== 36) {
                delete row.id;
            }
            return row;
        });
        const batchSize = 50;
        const totalBatches = Math.ceil(rows.length / batchSize);
        let allData = [];
        let errors = 0;
        for (let i = 0; i < rows.length; i += batchSize) {
            const batchNum = Math.floor(i / batchSize) + 1;
            const batch = rows.slice(i, i + batchSize);
            const { data, error } = await sb.from('leads').insert(batch).select();
            if (error) {
                console.error(`insertLeads batch ${batchNum}/${totalBatches} error:`, error);
                errors++;
            }
            else if (data) { allData = allData.concat(data); }
            if (onProgress) onProgress(batchNum, totalBatches, allData.length);
        }
        if (errors > 0) console.warn(`insertLeads: ${errors} batch(es) failed, ${allData.length}/${rows.length} rows inserted`);
        return allData.length > 0 ? allData : null;
    },

    async deleteLead(id) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('leads').delete().eq('id', id);
        if (error) {
            console.error('deleteLead error:', error, 'id:', id);
            window.toast('Error eliminando lead de Supabase: ' + error.message, 'error');
        }
    },

    async updateLeadStage(id, stage, progress) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('leads').update({ stage, progress, updated_at: new Date().toISOString() }).eq('id', id);
        if (error) console.error('updateLeadStage error:', error, 'id:', id);
    },

    async updateLeadHistorico(id) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('leads').update({ is_historico: true, updated_at: new Date().toISOString() }).eq('id', id);
        if (error) console.error('updateLeadHistorico error:', error, 'id:', id);
    },

    // --- CLIENTS ---
    async fetchClients() {
        if (!this.isConnected()) return null;
        let allData = [];
        let from = 0;
        const pageSize = 1000;
        while (true) {
            const { data, error } = await sb.from('clients').select('*')
                .range(from, from + pageSize - 1);
            if (error) { console.error('fetchClients error:', error); break; }
            if (!data || data.length === 0) break;
            allData = allData.concat(data);
            if (data.length < pageSize) break;
            from += pageSize;
        }
        return allData.length > 0 ? allData : null;
    },

    async upsertClient(clientKey, clientData) {
        if (!this.isConnected()) return null;
        const row = {
            client_key: clientKey,
            titular: clientData.titular,
            contacto: clientData.contacto || null,
            phone: clientData.phone || null,
            email: clientData.email || null,
            referido: clientData.referido || null,
            canal_alerta: clientData.canalAlerta || 'whatsapp'
        };
        const { data, error } = await sb.from('clients').upsert(row, { onConflict: 'client_key' }).select().maybeSingle();
        if (error) { console.error('upsertClient error:', error); return null; }
        return data;
    },

    async deleteClient(clientKey) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('clients').delete().eq('client_key', clientKey);
        if (error) console.error('deleteClient error:', error, 'key:', clientKey);
    },

    // --- TAREAS ---
    async fetchTareas() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('tareas').select('*').order('created_at', { ascending: false });
        if (error) { console.error('fetchTareas error:', error); return null; }
        return data;
    },

    async upsertTarea(tarea) {
        if (!this.isConnected()) return null;
        const row = {
            titulo: tarea.titulo,
            descripcion: tarea.descripcion || null,
            responsable: tarea.responsable || null,
            fecha_limite: tarea.fechaLimite || null,
            lead_id: tarea.leadId || null,
            prioridad: tarea.prioridad || 'normal',
            estado: tarea.estado || 'pendiente',
            creado_por: tarea.creadoPor || null,
            fecha_completada: tarea.fechaCompletada || null
        };
        if (tarea._supabaseId) row.id = tarea._supabaseId;
        const { data, error } = await sb.from('tareas').upsert(row).select().maybeSingle();
        if (error) { console.error('upsertTarea error:', error); return null; }
        return data;
    },

    async deleteTarea(id) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('tareas').delete().eq('id', id);
        if (error) console.error('deleteTarea error:', error, 'id:', id);
    },

    // --- COMENTARIOS ---
    async fetchComentarios(leadId) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('comentarios').select('*').eq('lead_id', leadId).order('created_at', { ascending: true });
        if (error) { console.error('fetchComentarios error:', error); return null; }
        return data;
    },

    async addComentario(leadId, autor, texto) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('comentarios').insert({ lead_id: leadId, autor, texto }).select().maybeSingle();
        if (error) { console.error('addComentario error:', error); return null; }
        return data;
    },

    // --- COTIZACIONES ---
    async fetchCotizaciones(leadId) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('cotizaciones').select('*').eq('lead_id', leadId).order('created_at', { ascending: false });
        if (error) { console.error('fetchCotizaciones error:', error); return null; }
        return data;
    },

    async upsertCotizacion(cotizacion) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('cotizaciones').upsert(cotizacion).select().maybeSingle();
        if (error) { console.error('upsertCotizacion error:', error); return null; }
        return data;
    },

    // --- FORM SUBMISSIONS ---
    async fetchFormSubmissions(status = 'new') {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('form_submissions').select('*').eq('status', status).order('created_at', { ascending: false });
        if (error) { console.error('fetchFormSubmissions error:', error); return null; }
        return data;
    },

    async updateFormSubmissionStatus(id, status, leadId = null) {
        if (!this.isConnected()) return;
        const update = { status, updated_at: new Date().toISOString() };
        if (leadId) update.lead_id = leadId;
        const { error } = await sb.from('form_submissions').update(update).eq('id', id);
        if (error) console.error('updateFormSubmissionStatus error:', error, 'id:', id);
    },

    async fetchAllFormSubmissions() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('form_submissions').select('*').order('created_at', { ascending: false });
        if (error) { console.error('fetchAllFormSubmissions error:', error); return null; }
        return data;
    },

    async updateFormSubmissionFields(id, fields) {
        if (!this.isConnected()) return;
        await sb.from('form_submissions').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id);
    },

    async deleteFormSubmission(id) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('form_submissions').delete().eq('id', id);
        if (error) console.error('deleteFormSubmission error:', error);
    },

    // --- FORM SESSIONS ---
    async fetchAllFormSessions() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('form_sessions').select('*').order('started_at', { ascending: false });
        if (error) { console.error('fetchAllFormSessions error:', error); return null; }
        return data;
    },

    // --- SETTINGS ---
    async fetchSettings() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('settings').select('*');
        if (error) { console.error('fetchSettings error:', error); return null; }
        const result = {};
        (data || []).forEach(row => {
            result[row.key] = row.value;
        });
        return result;
    },

    async saveSetting(key, value) {
        if (!this.isConnected()) return;
        await sb.from('settings').upsert({ key, value, updated_by: currentUser?.email || 'unknown' }, { onConflict: 'key' });
    },

    // --- COMPANIES ---
    async fetchCompanies() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('companies').select('*').order('name');
        if (error) { console.error('fetchCompanies error:', error); return null; }
        return data;
    },

    async upsertCompany(company) {
        if (!this.isConnected()) return null;
        const row = this._companyToRow(company);
        if (!row.id || typeof row.id !== 'string' || row.id.length !== 36) delete row.id;
        const { data, error } = await sb.from('companies').upsert(row, { onConflict: 'id' }).select().maybeSingle();
        if (error) { console.error('upsertCompany error:', error); return null; }
        return data;
    },

    async deleteCompany(id) {
        if (!this.isConnected()) return;
        await sb.from('companies').delete().eq('id', id);
    },

    // --- COMPANY BOOKINGS ---
    async fetchCompanyBookings() {
        if (!this.isConnected()) return null;
        let allData = [];
        let from = 0;
        const pageSize = 1000;
        while (true) {
            const { data, error } = await sb.from('company_bookings').select('*')
                .order('check_in', { ascending: false })
                .range(from, from + pageSize - 1);
            if (error) { console.error('fetchBookings error:', error); break; }
            if (!data || data.length === 0) break;
            allData = allData.concat(data);
            if (data.length < pageSize) break;
            from += pageSize;
        }
        return allData.length > 0 ? allData : null;
    },

    async upsertBooking(booking) {
        if (!this.isConnected()) return null;
        const row = this._bookingToRow(booking);
        const { data, error } = await sb.from('company_bookings').upsert(row, { onConflict: 'id' }).select().maybeSingle();
        if (error) { console.error('upsertBooking error:', error); return null; }
        return data;
    },

    async insertBookings(bookingsArray, onProgress) {
        if (!this.isConnected()) return null;
        const rows = bookingsArray.map(b => {
            const row = this._bookingToRow(b);
            if (!row.id || typeof row.id !== 'string' || row.id.length !== 36) delete row.id;
            return row;
        });
        const batchSize = 50;
        let allData = [];
        let errors = 0;
        for (let i = 0; i < rows.length; i += batchSize) {
            const batch = rows.slice(i, i + batchSize);
            const { data, error } = await sb.from('company_bookings').insert(batch).select();
            if (error) { console.error('insertBookings batch error:', error); errors++; }
            else if (data) { allData = allData.concat(data); }
            if (onProgress) onProgress(Math.floor(i / batchSize) + 1, Math.ceil(rows.length / batchSize), allData.length);
        }
        if (errors > 0) console.warn(`insertBookings: ${errors} batch(es) failed, ${allData.length}/${rows.length} rows inserted`);
        return allData.length > 0 ? allData : null;
    },

    async deleteBooking(id) {
        if (!this.isConnected()) return;
        await sb.from('company_bookings').delete().eq('id', id);
    },

    async updateBookingStage(id, stage) {
        if (!this.isConnected()) return;
        await sb.from('company_bookings').update({ stage, updated_at: new Date().toISOString() }).eq('id', id);
    },

    // --- REALTIME ---
    // All 6 callbacks are provided by the caller (setupRealtimeSubscriptions).
    // State-dependent logic stays in the caller, not in this module.
    subscribeToChanges({ onLeadChange, onTareaChange, onFormSubmission, onFormSession, onGuideSession, onBookingChange, onDashboardChange, onWhatsAppMessage }) {
        if (!this.isConnected()) return null;

        const channel = sb.channel('crm-realtime')
            .on('postgres_changes', { event: '*', schema: DB_SCHEMA, table: 'leads' }, (payload) => {
                console.log('Lead change:', payload.eventType, payload);
                if (onLeadChange) onLeadChange(payload);
            })
            .on('postgres_changes', { event: '*', schema: DB_SCHEMA, table: 'tareas' }, (payload) => {
                console.log('Tarea change:', payload.eventType, payload);
                if (onTareaChange) onTareaChange(payload);
            })
            .on('postgres_changes', { event: 'INSERT', schema: DB_SCHEMA, table: 'form_submissions' }, (payload) => {
                console.log('New form submission:', payload);
                if (onFormSubmission) onFormSubmission(payload);
            })
            .on('postgres_changes', { event: '*', schema: DB_SCHEMA, table: 'form_sessions' }, (payload) => {
                if (onFormSession) onFormSession(payload);
            })
            .on('postgres_changes', { event: '*', schema: DB_SCHEMA, table: 'guide_sessions' }, (payload) => {
                if (onGuideSession) onGuideSession(payload);
            })
            .on('postgres_changes', { event: '*', schema: DB_SCHEMA, table: 'company_bookings' }, (payload) => {
                console.log('Booking change:', payload.eventType, payload);
                if (onBookingChange) onBookingChange(payload);
            })
            .on('postgres_changes', { event: 'UPDATE', schema: DB_SCHEMA, table: 'client_dashboards' }, (payload) => {
                if (onDashboardChange) onDashboardChange(payload);
            })
            .on('postgres_changes', { event: 'INSERT', schema: DB_SCHEMA, table: 'whatsapp_messages' }, (payload) => {
                if (onWhatsAppMessage) onWhatsAppMessage(payload);
            })
            .subscribe();

        return channel;
    },

    // --- HELPERS ---
    _sanitizeDate(val) {
        if (!val) return null;
        const s = String(val).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
        const d = new Date(val);
        if (!isNaN(d.getTime()) && d.getFullYear() > 2000) {
            return d.toISOString().substring(0, 10);
        }
        return null;
    },

    _leadToRow(lead) {
        return {
            id: lead._supabaseId || undefined,
            client_key: lead.clientKey || null,
            titular: lead.titular,
            contacto: lead.contacto || null,
            phone: lead.phone || null,
            email: lead.email || null,
            type: lead.type || 'otro',
            stage: lead.stage || 'nuevo',
            source: lead.source || 'Manual',
            travel_date: this._sanitizeDate(lead.travelDate),
            travel_end: this._sanitizeDate(lead.travelEnd),
            observaciones: (() => {
                let obs = lead.observaciones || '';
                const tdText = lead.travelDate && !this._sanitizeDate(lead.travelDate) ? lead.travelDate : null;
                const teText = lead.travelEnd && !this._sanitizeDate(lead.travelEnd) ? lead.travelEnd : null;
                if (tdText) obs = (obs ? obs + '\n' : '') + 'Fecha viaje (texto): ' + tdText;
                if (teText) obs = (obs ? obs + '\n' : '') + 'Fecha fin (texto): ' + teText;
                return obs || null;
            })(),
            destination: lead.destination || null,
            platform: lead.platform || null,
            reservation_num: lead.reservationNum || null,
            detalle: lead.detalle || null,
            hotel: lead.hotel || null,
            dias_parque: lead.diasParque || null,
            tipo_parque: lead.tipoParque || null,
            tickets: lead.tickets || null,
            ticket_valid_from: this._sanitizeDate(lead.ticketValidFrom),
            ticket_valid_to: this._sanitizeDate(lead.ticketValidTo),
            park_hopper: lead.parkHopper || false,
            has_dining_plan: lead.hasDiningPlan || false,
            viajeros: lead.viajeros || null,
            total_venta: lead.totalVenta || 0,
            commission: lead.commission || 0,
            commission_70: lead.commission70 || 0,
            abono: lead.abono || 0,
            abonado: lead.abonado || 0,
            fecha_abono: this._sanitizeDate(lead.fechaAbono),
            fecha_pago_final: this._sanitizeDate(lead.fechaPagoFinal),
            progress: lead.progress || 0,
            responsable: lead.responsable || null,
            solicitud: lead.solicitud || null,
            seguimientos: lead.seguimientos || 0,
            lost_reason: lead.lostReason || null,
            form_data: lead.formData || lead.cotizacionRevisions > 0 || lead.cotizaciones?.length
                ? { ...(lead.formData || {}),
                    ...(lead.cotizacionRevisions > 0 ? { cotizacionRevisions: lead.cotizacionRevisions } : {}),
                    ...(lead.cotizaciones?.length ? { cotizaciones: lead.cotizaciones } : {}) }
                : null,
            is_historico: lead.stage === 'historico' || lead.stage === 'perdido' || (lead.isHistorico === true)
        };
    },

    _rowToLead(row) {
        const lead = {
            id: row.id,
            _supabaseId: row.id,
            clientKey: row.client_key,
            titular: row.titular,
            contacto: row.contacto,
            phone: row.phone,
            email: row.email,
            type: row.type,
            stage: row.stage || (row.is_historico ? 'historico' : 'nuevo'),
            source: row.source,
            travelDate: row.travel_date,
            travelEnd: row.travel_end,
            destination: row.destination,
            platform: row.platform,
            reservationNum: row.reservation_num,
            detalle: row.detalle,
            hotel: row.hotel,
            diasParque: row.dias_parque,
            tipoParque: row.tipo_parque,
            tickets: row.tickets,
            ticketValidFrom: row.ticket_valid_from,
            ticketValidTo: row.ticket_valid_to,
            parkHopper: row.park_hopper,
            hasDiningPlan: row.has_dining_plan,
            viajeros: row.viajeros,
            totalVenta: parseFloat(row.total_venta) || 0,
            commission: parseFloat(row.commission) || 0,
            commission70: parseFloat(row.commission_70) || 0,
            abono: parseFloat(row.abono) || 0,
            abonado: parseFloat(row.abonado) || 0,
            fechaAbono: row.fecha_abono,
            fechaPagoFinal: row.fecha_pago_final,
            progress: row.progress,
            responsable: row.responsable,
            solicitud: row.solicitud,
            observaciones: row.observaciones || '',
            seguimientos: row.seguimientos || 0,
            lostReason: row.lost_reason || null,
            formData: row.form_data || null,
            isHistorico: row.is_historico,
            createdAt: row.created_at,
            updatedAt: row.updated_at
        };
        // Hydrate multi arrays from formData JSONB
        if (lead.formData?.hotels) lead.hotels = lead.formData.hotels
        if (lead.formData?.parkEntries) lead.parkEntries = lead.formData.parkEntries
        if (lead.formData?.cruises) lead.cruises = lead.formData.cruises
        if (lead.formData?.grupoViajeros) lead.grupoViajeros = lead.formData.grupoViajeros
        if (lead.formData?.cotizacionRevisions) lead.cotizacionRevisions = lead.formData.cotizacionRevisions
        if (lead.formData?.cotizaciones) lead.cotizaciones = lead.formData.cotizaciones
        return lead;
    },

    _rowToTarea(row) {
        return {
            id: row.id,
            _supabaseId: row.id,
            titulo: row.titulo,
            descripcion: row.descripcion,
            responsable: row.responsable,
            fechaLimite: row.fecha_limite,
            leadId: row.lead_id,
            prioridad: row.prioridad,
            estado: row.estado,
            creadoPor: row.creado_por,
            fechaCreacion: row.created_at,
            fechaCompletada: row.fecha_completada
        };
    },

    _companyToRow(c) {
        return {
            id: c._supabaseId || undefined,
            name: c.name,
            contact_name: c.contactName || null,
            contact_email: c.contactEmail || null,
            contact_phone: c.contactPhone || null,
            address: c.address || null,
            default_commission_pct: c.defaultCommissionPct || 8.5,
            default_m2m_pct: c.defaultM2mPct || 70,
            notes: c.notes || null,
            active: c.active !== false
        };
    },

    _rowToCompany(row) {
        return {
            id: row.id,
            _supabaseId: row.id,
            name: row.name,
            contactName: row.contact_name,
            contactEmail: row.contact_email,
            contactPhone: row.contact_phone,
            address: row.address,
            defaultCommissionPct: parseFloat(row.default_commission_pct) || 8.5,
            defaultM2mPct: parseFloat(row.default_m2m_pct) || 70,
            notes: row.notes,
            active: row.active,
            createdAt: row.created_at
        };
    },

    _bookingToRow(b) {
        return {
            id: b._supabaseId || undefined,
            company_id: b.companyId || null,
            traveler_name: b.travelerName || 'N/A',
            client_name: b.clientName || null,
            reservation_num: b.reservationNum || null,
            hotel_name: b.hotelName || null,
            destination: b.destination || null,
            check_in: this._sanitizeDate(b.checkIn),
            check_out: this._sanitizeDate(b.checkOut),
            num_guests: b.numGuests || 1,
            num_rooms: b.numRooms || 1,
            total_purchase: b.totalPurchase || 0,
            commission_rate: b.commissionRate || null,
            commission_amount: b.commissionAmount || 0,
            m2m_pct: b.m2mPct || 70,
            m2m_amount: b.m2mAmount || 0,
            stage: b.stage || 'pendiente',
            source_file: b.sourceFile || null,
            source_format: b.sourceFormat || null,
            booking_date: this._sanitizeDate(b.bookingDate),
            detail: b.detail || null,
            state_abbr: b.stateAbbr || null,
            notes: b.notes || null
        };
    },

    _rowToBooking(row) {
        return {
            id: row.id,
            _supabaseId: row.id,
            companyId: row.company_id,
            travelerName: row.traveler_name,
            clientName: row.client_name,
            reservationNum: row.reservation_num,
            hotelName: row.hotel_name,
            destination: row.destination,
            checkIn: row.check_in,
            checkOut: row.check_out,
            numGuests: row.num_guests,
            numRooms: row.num_rooms,
            totalPurchase: parseFloat(row.total_purchase) || 0,
            commissionRate: parseFloat(row.commission_rate) || 0,
            commissionAmount: parseFloat(row.commission_amount) || 0,
            m2mPct: parseFloat(row.m2m_pct) || 70,
            m2mAmount: parseFloat(row.m2m_amount) || 0,
            stage: row.stage || 'pendiente',
            sourceFile: row.source_file,
            sourceFormat: row.source_format,
            bookingDate: row.booking_date,
            detail: row.detail,
            stateAbbr: row.state_abbr,
            notes: row.notes,
            createdAt: row.created_at,
            updatedAt: row.updated_at
        };
    },

    // --- CLIENT DASHBOARDS ---
    async fetchClientDashboard(leadId) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('client_dashboards').select('*').eq('lead_id', leadId).maybeSingle();
        if (error) { console.error('fetchClientDashboard error:', error); return null; }
        return data;
    },

    async fetchClientDashboardByToken(token) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('client_dashboards').select('*').eq('token', token).maybeSingle();
        if (error) { console.error('fetchClientDashboardByToken error:', error); return null; }
        return data;
    },

    async createClientDashboard(leadId, prefillData) {
        if (!this.isConnected()) return null;
        const token = Array.from(crypto.getRandomValues(new Uint8Array(12)),
            b => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[b % 62]).join('');
        const timeline = [{
            key: 'reserva_confirmada',
            type: 'milestone',
            visible: true,
            data: { title: 'Reserva Confirmada', description: 'Tu viaje está confirmado.' },
            addedAt: new Date().toISOString()
        }];
        const { data, error } = await sb.from('client_dashboards').insert({
            token,
            lead_id: leadId,
            prefill_data: prefillData,
            timeline
        }).select().maybeSingle();
        if (error) { console.error('createClientDashboard error:', error); return null; }
        return data;
    },

    async updateClientDashboard(dashId, fields) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('client_dashboards')
            .update({ ...fields, updated_at: new Date().toISOString() })
            .eq('id', dashId).select().maybeSingle();
        if (error) { console.error('updateClientDashboard error:', error); return null; }
        return data;
    },

    async addDashboardTimelineItem(dashId, item) {
        if (!this.isConnected()) return null;
        // Fetch current timeline, append, update
        const { data: current } = await sb.from('client_dashboards').select('timeline').eq('id', dashId).maybeSingle();
        if (!current) return null;
        const timeline = [...(current.timeline || []), { ...item, addedAt: new Date().toISOString() }];
        return this.updateClientDashboard(dashId, { timeline });
    },

    async updateDashboardTimeline(dashId, timeline) {
        if (!this.isConnected()) return null;
        return this.updateClientDashboard(dashId, { timeline });
    },

    // --- CLIENT DOCUMENT STORAGE ---
    async uploadClientDocument(leadId, blob, filename) {
        if (!this.isConnected()) return null;
        const path = `${leadId}/${filename}`;
        const { data, error } = await sb.storage.from('client-documents').upload(path, blob, {
            cacheControl: '3600',
            upsert: true
        });
        if (error) { console.error('uploadClientDocument error:', error); return null; }
        const { data: urlData } = sb.storage.from('client-documents').getPublicUrl(path);
        return urlData?.publicUrl || null;
    },

    async deleteClientDocument(leadId, filename) {
        if (!this.isConnected()) return;
        const path = `${leadId}/${filename}`;
        const { error } = await sb.storage.from('client-documents').remove([path]);
        if (error) console.error('deleteClientDocument error:', error);
    },

    // --- WHATSAPP MESSAGES ---
    async fetchWhatsAppMessages(leadId) {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('whatsapp_messages')
            .select('*')
            .eq('lead_id', leadId)
            .order('created_at', { ascending: true });
        if (error) { console.error('fetchWhatsAppMessages error:', error); return null; }
        return data;
    },

    async fetchUnmatchedMessages() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('whatsapp_messages')
            .select('*')
            .is('lead_id', null)
            .order('created_at', { ascending: false });
        if (error) { console.error('fetchUnmatchedMessages error:', error); return null; }
        return data;
    },

    async linkMessageToLead(messageId, leadId) {
        if (!this.isConnected()) return;
        const { error } = await sb.from('whatsapp_messages')
            .update({ lead_id: leadId, updated_at: new Date().toISOString() })
            .eq('id', messageId);
        if (error) console.error('linkMessageToLead error:', error);
    },

    async fetchWhatsAppSummaries() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('whatsapp_messages')
            .select('lead_id, direction, created_at')
            .not('lead_id', 'is', null)
            .order('created_at', { ascending: false });
        if (error) { console.error('fetchWhatsAppSummaries error:', error); return null; }
        const summaries = {};
        for (const row of (data || [])) {
            if (!summaries[row.lead_id]) {
                summaries[row.lead_id] = {
                    lastMessageAt: row.created_at,
                    lastDirection: row.direction,
                    messageCount: 1
                };
            } else {
                summaries[row.lead_id].messageCount++;
            }
        }
        return summaries;
    },

    // --- TEAM MEMBERS ---
    async fetchTeamMembers() {
        if (!this.isConnected()) return null;
        const { data, error } = await sb.from('team_members').select('*').eq('activo', true).order('orden', { ascending: true, nullsFirst: false }).order('created_at');
        if (error) { console.error('fetchTeamMembers:', error); return null; }
        return data;
    },

    async upsertTeamMember(member) {
        if (!this.isConnected()) return null;
        const row = { nombre: member.nombre, rol: member.rol || null, email: member.email || null, orden: member.orden != null ? member.orden : null, activo: true };
        if (member._supabaseId) row.id = member._supabaseId;
        const { data, error } = await sb.from('team_members').upsert(row).select().single();
        if (error) { console.error('upsertTeamMember:', error); return null; }
        return data;
    },

    async deleteTeamMember(supabaseId) {
        if (!this.isConnected()) return;
        // Soft delete — set activo = false
        const { error } = await sb.from('team_members').update({ activo: false }).eq('id', supabaseId);
        if (error) console.error('deleteTeamMember:', error);
    }
};

// Bridge: expose for inline module
window.supabaseDB = supabaseDB
